/**
 * Minimal IndexedDB wrapper.
 *
 * Hand-rolled rather than pulling in a wrapper library: this needs four operations on
 * two stores, and the promise-wrapping below is the whole of what a dependency would
 * add. IndexedDB rather than localStorage because the persisted query cache is far past
 * localStorage's ~5 MB ceiling, and because localStorage is synchronous — writing the
 * book of business to it would block the main thread on every save.
 */

const DB_NAME = 'ias-offline';
const DB_VERSION = 1;

export const STORE_OUTBOX = 'outbox';
export const STORE_CACHE = 'cache';

let connection: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  connection ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return connection;
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const request = operation(transaction.objectStore(store));
        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => reject(request.error);
      }),
  );
}

export const idb = {
  get: <T>(store: string, key: IDBValidKey) => run<T | undefined>(store, 'readonly', (s) => s.get(key)),
  getAll: <T>(store: string) => run<T[]>(store, 'readonly', (s) => s.getAll()),
  put: <T>(store: string, value: T, key?: IDBValidKey) =>
    run<IDBValidKey>(store, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key))),
  delete: (store: string, key: IDBValidKey) => run<undefined>(store, 'readwrite', (s) => s.delete(key)),
  clear: (store: string) => run<undefined>(store, 'readwrite', (s) => s.clear()),
};

/** True when the browser can persist at all — private windows and old browsers cannot. */
export function isStorageAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}
